import React, { useEffect, useState, useMemo } from 'react';
import { CnCheckbox, CnTree, CnBox } from '@sto/cn-ui';
import styles from './index.module.scss';

interface ICustomTreeProps {
  dataSource?: any[];
  [key: string]: any;
}

/**
 * 递归获取所有节点的key，包括children
 * @param data 数据源
 * @returns 所有节点的key数组
 */
const getAllKeysRecursively = (data: any[]): string[] => {
  const keys: string[] = [];
  
  const traverse = (items: any[]) => {
    items?.forEach((item) => {
      if (item.key) {
        keys.push(item.key);
      }
      // 递归遍历children
      if (item.children && Array.isArray(item.children) || (item.props && item.props.children && Array.isArray(item.props.children))) {
        traverse(item.children || item.props.children);
      }
    });
  };
  
  traverse(data);
  return keys;
};

/**
 * 自定义树形组件
 * @param dataSource 数据源
 * @param value 值
 * @param onChange 变化事件
 * @returns 
 */
const CustomTree = ({
  mode = 'multiple', // single 单选
  defaultValue,
  value, 
  onChange,
  dataSource,
  showAllChecked = true, // 是否显示全选
  showParentCheck = true, // 是否显示每层父节点的全选
  disabled = false, // 是否禁用
  ...props
}: ICustomTreeProps) => {
  const [treeValues, setTreeValues] = useState<any>(null);
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false);
  const [isIndeterminate, setIsIndeterminate] = useState<boolean>(false);

  /**
   * 格式化数据源
   * @returns 格式化数据源
   */
  const formatDataSource = useMemo(() => {
    if (!dataSource) {
      return [];
    }
    /**递归处理数据源，添加disabled和key */
    const loop = (data: any[]): any[] =>
      data.map((item) => {
        const newItem = { ...item };
        if (newItem.children && Array.isArray(newItem.children)) {
          newItem.children = loop(newItem.children);
        }
        if (disabled) {
          newItem.disabled = disabled;
        }
        newItem.key = item.value || item.key;
        return newItem;
      });
    return loop(dataSource);
  }, [dataSource, disabled]);

  /** 监听value变化 */
  useEffect(() => {
    setTreeValues(value || []);
    const allKeys = getAllKeysRecursively(formatDataSource || []);
    const currentValueLength = (value || []).length;
    setIsAllChecked(currentValueLength === allKeys.length && allKeys.length > 0);
    setIsIndeterminate(currentValueLength > 0 && currentValueLength < allKeys.length);
  }, [value, formatDataSource]);

  /**
   * 全选变化事件
   * @param isChecked 是否全选
   */
  const handleAllCheckedChange = (isChecked: boolean) => {
    setIsAllChecked(isChecked);
    setIsIndeterminate(false);
    if (isChecked) {
      const allKeys = getAllKeysRecursively(formatDataSource || []);
      onChange(allKeys);
    } else {
      onChange([]);
    }
  }

  /**
   * 树形组件变化事件
   * @param value 值
   */
  const handleTreeChange = (value: any) => {
    onChange(value);
  }

  /**
   * 处理父节点的全选事件
   * @param isChecked 值
   */
  const handleParentCheckChange = (isChecked: boolean, node: any) => {
    const { key, children } = node;
    const allChildKeys = getAllKeysRecursively(children || []);
    const currentValue = value || [];
    
    if (isChecked) {
      // 选中：添加所有子节点，去重
      const newKeys = [...currentValue, key, ...allChildKeys];
      const deduplicatedKeys = [...new Set(newKeys)];
      onChange(deduplicatedKeys);
    } else {
      // 取消选中：移除所有子节点
      const newKeys = currentValue.filter((itemKey: string) => !allChildKeys.includes(itemKey) && itemKey !== key);
      onChange(newKeys);
    }
  }

  /**
   * 父节点全选配置
   * @param node 节点
   * @returns 父节点全选配置
   */
  const customParentCheckConfig = useMemo(() => {
    if (!showParentCheck) {
      return {};
    }
  
    return {
      checkStrictly: true, // 父子节点选中状态不再关联
      selectable: false,
      isLabelBlock: true,
      isNodeBlock: true,
      labelRender: (node: any) => {
        const { key, label, children } = node || {};
        const currentValue = value || [];
        const allChildKeys = getAllKeysRecursively(children || []);
        const allKeys = [key, ...allChildKeys]; // 所有子节点key+父节点key
        const isChecked = allKeys.length > 0 && allKeys.every((key: string) => currentValue.includes(key));
        const isIndeterminate = !isChecked && allKeys.some((key: string) => currentValue.includes(key));
        return <CnBox direction="row" align="center" justify="space-between">
          <div>{label}</div>
          {children?.length > 0 && <CnCheckbox 
           checked={isChecked} 
           indeterminate={isIndeterminate}
           onChange={(v) => handleParentCheckChange(v, node)}
           disabled={disabled}
          >
            全选
          </CnCheckbox>}
        </CnBox>
      }
    }
  }, [showParentCheck, value, disabled]);

  return <div className={styles.customTreeBox}>
    {showAllChecked && (
      <CnCheckbox 
        checked={isAllChecked} 
        indeterminate={isIndeterminate} 
        onChange={handleAllCheckedChange}
        disabled={disabled}
      >
        全选
      </CnCheckbox>
    )}
    <CnTree
      // defaultExpandAll
      // defaultExpandedKeys={['biz_num', 'per_net_contrib']}
      checkable
      checkedKeys={treeValues}
      onCheck={handleTreeChange}
      dataSource={formatDataSource}
      {...customParentCheckConfig}
      {...props}
   />
  </div>;
};

export default CustomTree;